'use client';

import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
  type MutationMeta,
  type QueryMeta,
} from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

import { notify } from '@/services/notify/notify';
import { validateApiMeta } from '@/services/react-query/api-meta';
import { dispatchMetaError } from '@/services/react-query/dispatch-meta-error';
import {
  getApiErrorStatusCode,
  validateApiError,
} from '@/services/react-query/validate-api-error';

const MAX_FAILURE = 3;

const showFallbackToast = (error: unknown, meta: unknown, cause?: unknown) => {
  notify.alert.error('Something went wrong, try again later');
  console.error({ error, fallbackError: cause, meta });
};

const handleAppError = (
  error: unknown,
  meta: QueryMeta | MutationMeta | undefined
) => {
  try {
    const apiError = validateApiError(error);
    const validatedMeta = validateApiMeta(meta);
    if (!validatedMeta) {
      showFallbackToast(error, meta);
      return;
    }
    dispatchMetaError(apiError, validatedMeta);
  } catch (fallbackError) {
    showFallbackToast(error, meta, fallbackError);
  }
};

export const ReactQueryProvider = ({ children }: { children: ReactNode }) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              const status = getApiErrorStatusCode(error);
              if (status !== undefined && status >= 400 && status < 500)
                return false;
              return failureCount < MAX_FAILURE;
            },
          },
        },

        queryCache: new QueryCache({
          onError: (error, { meta }) => {
            handleAppError(error, meta);
          },
        }),

        mutationCache: new MutationCache({
          onError: (error, _variables, _context, { meta }) => {
            handleAppError(error, meta);
          },
        }),
      })
  );

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
